/** @jsxImportSource @emotion/react */
import * as React from "react";
import styled from "@emotion/styled";
import {
  useSearchParams,
  createSearchParams,
  URLSearchParamsInit,
} from "react-router-dom";
import {
  Button,
  FlexRow,
  Grid,
  IconChevron,
  IconFilters,
  LoadingSpinner,
  SiteContainer,
} from "atoms";
import exploreHeadlineSrc from "assets/images/explore-headline.svg";
import { Head } from "features/misc";
import { ReviewableCard } from "features/reviewable/components/ReviewableCard";
import { scrollToTop } from "utils/scroll";
import { useInfiniteSearch } from "../api/useSearch";
import { parseFilters } from "../utils/parseFilters";
import { updateFilters } from "../utils/updateFilters";
import { validateFilterForm } from "../utils/validateFilterForm";
import { ActiveFilters } from "./ActiveFilters";
import { MostExplored } from "./MostExplored";
import { SearchBar } from "./SearchBar";
import { SearchFilters, FilterErrors } from "./SearchFilters";
import { SearchSort } from "./SearchSort";

const formId = "project-search-form";

function Search() {
  const formRef = React.useRef(null);
  const [errors, setErrors] = React.useState<null | FilterErrors>(null);

  const [isShowingFilters, setIsShowingFilters] = React.useState(false);
  const toggleIsShowingFilters = () => setIsShowingFilters(!isShowingFilters);

  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data,
    isError,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    error: searchError,
  } = useInfiniteSearch({ ...parseFilters(searchParams), perPage: 12 });

  // When search params change in URL
  React.useLayoutEffect(() => {
    if (formRef.current) {
      // Convert search params into usable object, e.g. combine like tags into an array
      const parsed = parseFilters(searchParams);
      // Update filter inputs with parsed values
      updateFilters(formRef.current, parsed);
      scrollToTop();
    }
  }, [searchParams]);

  // Submit filter form with new filters
  const onSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const validationErrors = validateFilterForm(formData);
    if (validationErrors) {
      setErrors(validationErrors);
    } else {
      // If previous errors existed, remove them from UI
      if (errors) setErrors(null);
      const parsed = parseFilters(formData);
      const params = createSearchParams(parsed as URLSearchParamsInit);
      // If sort option is selected, persist it while updating filter params
      const sort = searchParams.get("sort");
      if (sort) {
        params.append("sort", sort);
      }
      setSearchParams(params);
      setIsShowingFilters(false);
    }
  };

  const loadMore = () => {
    fetchNextPage();
  };

  return (
    <SiteContainer>
      <Head
        title="Explore"
        shareTitle="Explore Projects and Artists on the HUG"
      />

      <Main>
        <Header>
          <HeaderImg src={exploreHeadlineSrc} alt="Explore" />

          <SSearchBar name="name" enterKeyHint="search" form={formId} />

          {isError && (
            <FormError role="alert">
              {searchError.errorCode === "BAD_PARAMETER"
                ? "Sorry, we couldn't understand your filters."
                : "Sorry, something went wrong"}
            </FormError>
          )}
        </Header>

        <FiltersToggleWrapper>
          <FiltersToggle
            isShowing={isShowingFilters}
            onClick={toggleIsShowingFilters}
            aria-expanded={isShowingFilters ? "true" : "false"}
          >
            <IconFilters />
            <span>{isShowingFilters ? "Hide" : "Show"} Filters</span>
            <IconChevron />
          </FiltersToggle>
        </FiltersToggleWrapper>

        <Tags>
          <ActiveFilters />
        </Tags>

        <Summary>
          <MobileFiltersToggle
            onClick={toggleIsShowingFilters}
            isShowing={isShowingFilters}
            aria-expanded={isShowingFilters ? "true" : "false"}
          >
            Filter&nbsp;by
            <IconChevron />
          </MobileFiltersToggle>
          <SearchSort />
        </Summary>

        <SSearchFilters
          formId={formId}
          formRef={formRef}
          onSubmit={onSubmit}
          isLoading={isLoading}
          errors={errors}
          isShowing={isShowingFilters}
          breakpoint={bpDesktop}
        />

        <Results isShowing={isShowingFilters}>
          <Grid>
            {data?.pages?.map((page) =>
              page.data.map((project) => (
                <ReviewableCard key={project.id} entity={project} />
              ))
            )}
          </Grid>

          {(isLoading || isFetching) && <LoadingSpinner />}
          {hasNextPage && !isLoading && (
            <LoadMoreButton
              type="button"
              onClick={loadMore}
              disabled={isFetching}
              variant="gradient"
              css={{ display: "block", margin: "2rem auto" }}
            >
              Load More...
            </LoadMoreButton>
          )}
        </Results>

        <MostExplored />
      </Main>
    </SiteContainer>
  );
}

const bpDesktop = "@media (min-width: 1100px)";
const bpMobile = "@media (max-width: 1099px)";
const filtersWidth = "250px";

const Main = styled.main(({ theme }) => ({
  [bpDesktop]: {
    display: "grid",
    gap: "2rem 3rem",
    gridTemplateColumns: `${filtersWidth} auto`,
    gridTemplateAreas: `"header header"
      "tags tags"
      "toggle summary"
      "filters results"
      "footer footer"`,
  },
}));

// const stickerWidth = "4rem";

const Header = styled.header(({ theme }) => ({
  //  boxShadow: theme.boxShadow.light,
  // padding: "1rem",
  gridArea: "header",
  margin: "0 auto",
  maxWidth: "600px",
  minWidth: "40vw",
  [bpDesktop]: {
    // boxShadow: "none",
    // padding: `4rem 0 2rem ${stickerWidth}`,
  },
}));

const HeaderImg = styled.img({
  // display: "none",
  display: "block",
  maxWidth: 400,
  minWidth: 250,
  marginBottom: "1rem",
  marginLeft: "auto",
  marginRight: "auto",
  width: "70%",
});

const SSearchBar = styled(SearchBar)({
  marginTop: 0,
  color: "black",
  "&:placeholder": {
    color: "rgb(0 0 0 / .6)",
  },
  [bpDesktop]: {
    // paddingRight: stickerWidth,
  },
});

const FormError = styled.p(({ theme }) => ({
  color: theme.colors.primary,
  fontWeight: "bold",
  fontStyle: "italic",
  marginLeft: theme.spacing.md,
}));

const FiltersToggleWrapper = styled.div({
  alignItems: "center",
  display: "flex",
  gridArea: "toggle",
});

interface FiltersToggleProps {
  isShowing: boolean;
}

// Prevent FiltersToggleProps, used for styles only, being forwarded to custom components
const filtersToggleOptions = {
  shouldForwardProp: (prop: string) => prop !== "isShowing",
};

const FiltersToggle = styled(Button, {
  shouldForwardProp: (prop) => prop !== "isShowing",
})<FiltersToggleProps>(({ isShowing, theme }) => ({
  display: "none",
  [bpDesktop]: {
    ...theme.fonts.body,
    alignItems: "center",
    // background: isShowing ? theme.colors.bg : theme.colors.bgLight,
    // border: `2px solid ${isShowing ? theme.colors.bg : theme.colors.fg}`,
    cursor: "pointer",
    display: "flex",
    fontSize: theme.fontSizes.xs,
    justifyContent: "flex-start",
    padding: "0.8rem 1.2rem 0.8rem 1rem",
    width: "100%",
    "& span": {
      flex: "1 0 50%",
      fontWeight: "bold",
      paddingLeft: "0.5rem",
      textAlign: "left",
    },
    "& svg:last-of-type": {
      transform: `rotate(${isShowing ? "270deg" : "90deg"})`,
    },
    // "&:focus-visible": {
    //   outlineOffset: "2px",
    //   outline: `2px solid ${theme.colors.fg}`,
    // },
  },
}));

const Tags = styled.div({
  gridArea: "tags",
});

const Summary = styled(FlexRow)({
  alignItems: "center",
  justifyContent: "flex-end",
  gridArea: "summary",
  padding: "2rem 0",
  position: "relative",
  "&>*": {
    flex: "0 0 auto",
  },
  [bpDesktop]: {
    padding: 0,
  },
});

const MobileFiltersToggle = styled(
  Button,
  filtersToggleOptions
)<FiltersToggleProps>(({ isShowing, theme }) => ({
  alignItems: "center",
  display: "flex",
  flex: "0 0 auto",
  fontSize: theme.fontSizes.xs,
  gap: "0.5rem",
  svg: {
    transform: isShowing ? "rotate(0deg)" : "rotate(180deg)",
  },
  [bpDesktop]: {
    display: "none",
  },
  "&::after": {
    display: isShowing ? "block" : "none",
    content: "''",
    position: "fixed",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    "&:hover": {
      background: "rgba(255, 0, 0, 0.5)",
      opacity: 1,
    },
  },
}));

const SSearchFilters = styled(
  SearchFilters,
  filtersToggleOptions
)<FiltersToggleProps>(({ isShowing, theme }) => ({
  background: theme.colors.bgLight,
  opacity: isShowing ? 1 : 0,
  transition: "opacity 0.3s ease, transform 0.2s ease",
  zIndex: 11,
  [bpMobile]: {
    borderRadius: theme.borderRadius.md,
    boxShadow: isShowing ? `${theme.boxShadow.dark}` : "none",
    height: isShowing ? "auto" : 0,
    overflow: "hidden",
    padding: isShowing ? "1rem" : 0,
    position: "absolute",
    right: "1rem",
    width: "min(20rem, calc(100vw - 2rem))",
    zIndex: 11,
    transform: "translateY(-1.5rem)",
  },
  [bpDesktop]: {
    boxShadow: "none",
    gridArea: "filters",
    overflow: "hidden",
    padding: "0 0 0 1rem",
    marginLeft: "-1rem",
    maxHeight: "unset",
    pointerEvents: isShowing ? "auto" : "none",
    transform: isShowing ? "translateX(0)" : "translateX(-100%)",
  },
}));

const Results = styled.div<FiltersToggleProps>(({ isShowing }) => ({
  gridArea: "results",
  transition: "margin 0.2s ease",
  [bpDesktop]: {
    marginLeft: isShowing ? 0 : `calc(-${filtersWidth} - 3rem)`,
  },
}));

const LoadMoreButton = styled(Button)({
  marginTop: "2rem",
});

export { Search };
